<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-pencil-square me-2"></i>Edit Product
      </h4>
      <div>
        <button class="btn btn-outline-secondary" @click="$router.go(-1)">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <div class="product-form-content fade-in">
        <form @submit.prevent="submitForm">
          <!-- Basic Information Section -->
          <div class="border-0 shadow-sm mb-4">
            <div class="card-header bg-light py-3">
              <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Basic Information</h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="form-group col-md-3">
                  <label for="code" class="form-label fw-bold">Product No:</label>
                  <input type="text" class="form-control" placeholder="Product No" v-model="product.code" readonly>
                </div>
                <div class="form-group col-md-3">
                  <label for="sku" class="form-label fw-bold">SKU:</label>
                  <input type="text" class="form-control" placeholder="SKU" v-model="product.sku">
                </div>
                <div class="form-group col-md-6">
                  <label for="name" class="form-label fw-bold">Name:</label>
                  <input type="text" class="form-control" placeholder="Name" v-model="product.name">
                </div>
                <div class="form-group col-md-6">
                  <label for="validationTags" class="form-label fw-bold h5">Tags: <span class="text-black-50 fst-italic">(optional)</span></label>
                  <vue-tags-input
                      :add-on-key="[13, 32, ',']"
                      :tags="tags"
                      autocomplete-min-length="0"
                      style="background-color: #f2f2f2;
                        border: 2px solid #f2f2f2;
                        border-radius: 10px;
                        max-width: 100% !important;"
                      placeholder="search or type new tag"
                      :autocomplete-filter-duplicates="true"
                      :allow-edit-tags="true"
                      :autocomplete-always-open="showSuggestions"
                      :autocomplete-items="filteredItems"
                      @tags-changed="newTags => tags = newTags"
                  />
                  <span class="text-black-50 fst-italic" style="font-size: 15px">press enter, space or comma to add tag</span>
                </div>
                <div class="form-group col-md-6">
                  <label for="description" class="form-label fw-bold">Description: <span class="text-black-50 fst-italic small">(optional)</span></label>
                  <textarea class="form-control" placeholder="Description" v-model="product.description" rows="2"></textarea>
                </div>
              </div>
            </div>
          </div>

          <!-- Classification Section -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-light py-3">
              <h5 class="mb-0"><i class="bi bi-diagram-3 me-2"></i>Classification & Relationships</h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="form-group col-md-6">
                  <label for="category" class="form-label fw-bold">Category:</label>
                  <div class="input-group">
                    <select class="form-select" v-model="product.category_id" data="">
                      <option value="" disabled selected>Select category</option>
                      <option v-for="category in categories" :key="category.id" :value="category.id">{{category.name}}</option>
                    </select>
                    <button v-if="profile.role !== 'user'" type="button" class="btn btn-outline-success" @click="addCategory">
                      <i class="bi bi-plus-lg"></i> Add
                    </button>
                  </div>
                </div>
                <div class="form-group col-md-6">
                  <label for="brand" class="form-label fw-bold">Brand: <span class="text-muted fst-italic small fw-normal">(optional)</span></label>
                  <div class="input-group">
                    <select id="brand" class="form-select" v-model="product.brand_id">
                      <option :value="null">No brand</option>
                      <option v-for="brand in brands" :key="brand.id" :value="brand.id">{{ brand.name }}</option>
                    </select>
                    <button v-if="profile.role !== 'user'" type="button" class="btn btn-outline-success" @click="$router.push('/brands')">
                      <i class="bi bi-plus-lg"></i> Add
                    </button>
                  </div>
                </div>
                <div class="form-group col-md-6">
                  <label for="unit" class="form-label fw-bold">Unit:</label>
                  <div class="input-group">
                    <select class="form-select" v-model="product.unit_id" data="">
                      <option value="" disabled selected>Select unit</option>
                      <option v-for="unit in units" :key="unit.id" :value="unit.id">{{unit.name}}</option>
                    </select>
                    <button v-if="profile.role !== 'user'" type="button" class="btn btn-outline-success" @click="addUnit">
                      <i class="bi bi-plus-lg"></i> Add
                    </button>
                  </div>
                </div>
                <div class="form-group col-md-6">
                  <label for="tax_category" class="form-label fw-bold">Tax Category:</label>
                  <select class="form-select" v-model="product.tax_category_id" data="">
                    <option value="" disabled selected>Select tax category</option>
                    <option v-for="category in taxCategories" :key="category.id" :value="category.id">{{category.rate}}% - {{category.description}}</option>
                  </select>
                </div>
                <div class="form-group col-md-6">
                  <label for="supplier" class="form-label fw-bold">Supplier:</label>
                  <div class="input-group">
                    <select class="form-select" v-model="product.supplier_id" data="">
                      <option value="" disabled selected>Select supplier</option>
                      <option v-for="supplier in suppliers" :key="supplier.id" :value="supplier.id">{{supplier.name}}</option>
                    </select>
                    <button v-if="profile.role !== 'user'" type="button" class="btn btn-outline-success" @click="addSupplier">
                      <i class="bi bi-plus-lg"></i> Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Inventory & Pricing Section -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-light py-3">
              <h5 class="mb-0"><i class="bi bi-currency-dollar me-2"></i>Inventory & Pricing</h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="form-group col-md-3">
                  <label for="stock" class="form-label fw-bold">Current Stock:</label>
                  <div class="input-group">
                    <input type="number" class="form-control" placeholder="Current Stock" v-model="product.stock">
                  </div>
                </div>
                <div class="form-group col-md-3">
                  <label for="reorder" class="form-label fw-bold">Reorder Level:</label>
                  <div class="input-group">
                    <input type="number" class="form-control" placeholder="Reorder Level" v-model="product.reorder">
                  </div>
                </div>
                <div class="form-group col-md-3">
                  <label for="buy" class="form-label fw-bold">Buying Price:</label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-cash"></i></span>
                    <input type="number" class="form-control" placeholder="Buying Price" v-model="product.buy">
                  </div>
                </div>
                <div class="form-group col-md-3">
                  <label for="sell" class="form-label fw-bold">Selling Price:</label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-tag"></i></span>
                    <input type="number" class="form-control" placeholder="Selling Price" v-model="product.sell">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Status Section -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-light py-3">
              <h5 class="mb-0"><i class="bi bi-toggles me-2"></i>Status</h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-6">
                  <label for="productStatus" class="form-label fw-bold">Product Status:</label>
                  <select id="productStatus" class="form-select" v-model="product.status" data="">
                    <option value="active">Active (Available for sales and restocking)</option>
                    <option value="discontinued">Discontinued (No longer restocked, hidden from POS)</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <small class="text-muted d-block mt-1">
                    <span v-if="product.status === 'discontinued'" class="text-warning">
                      <i class="bi bi-exclamation-triangle me-1"></i>Discontinued products are excluded from POS sales and restock alerts.
                    </span>
                    <span v-else-if="product.status === 'active'" class="text-success">
                      <i class="bi bi-check-circle me-1"></i>Product is active and available for selling.
                    </span>
                    <span v-else class="text-secondary">
                      <i class="bi bi-dash-circle me-1"></i>Product is inactive.
                    </span>
                  </small>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="d-flex justify-content-center gap-3 mt-4 mb-3">
            <button class="btn btn-outline-secondary px-4" @click="$router.go(-1)">
              <i class="bi bi-arrow-left me-1"></i> Back
            </button>
            <button v-if="profile.role !== 'user'" class="btn btn-primary px-4" @click="submitForm">
              <i class="bi bi-save me-1"></i> Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  </Card>
</template>

<script>
import productService from "@/api/productService";
import categoryService from "@/api/categoryService";
import brandService from "@/api/brandService";
import unitService from "@/api/unitService";
import supplierService from "@/api/supplierService";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import taxCategoryService from "@/api/taxCategoryService";
import loadingButton from "@/utils/loadingButton";
import VueTagsInput from '@sipec/vue3-tags-input';

export default {
  name: "EditProduct",
  components: {
    VueTagsInput,
  },
  data() {
    return {
      loading: true,
      categories: [],
      brands: [],
      units: [],
      suppliers: [],
      taxCategories: [],
      product: {},
      profile: {},
      showSuggestions: false,
      tag: '',
      tags: [],
      suggestions: []
    }
  },
  methods: {
    submitForm: async function (e) {
      loadingButton(e)
      await productService.updateProduct(this.product, this.$route.params.id)
      loadingButton(e, false)
      this.$router.push('/product/' + this.$route.params.id)
    },
    addCategory: function () {
      router.push({
        path: 'categories',
        query: { add: 'true' }
      })
    },
    addSupplier: function () {
      router.push({
        path: 'suppliers',
        query: { add: 'true' }
      })
    },
    addUnit: function () {
      router.push({
        path: 'work-units',
        query: { add: 'true' }
      })
    },
  },

  created: async function () {
    try {
      this.loading = true
      this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
      this.product = await productService.getProduct(this.$route.params.id)
      this.tags = this.product.tags === null ? [] : this.product.tags.split(',')
      this.suggestions = this.product.uniqueTags
      
      const categoriesResponse = await categoryService.getCategories({ per_page: 500 })
      this.categories = categoriesResponse?.data || []

      const brandsResponse = await brandService.getBrands({ all: 1 })
      this.brands = Array.isArray(brandsResponse) ? brandsResponse : []
      
      const suppliersResponse = await supplierService.getSuppliers({ per_page: 500 })
      this.suppliers = suppliersResponse?.data || []
      
      const taxCategoriesResponse = await taxCategoryService.getTaxCategories()
      this.taxCategories = taxCategoriesResponse || []
      
      const unitsResponse = await unitService.getUnits({ per_page: 500 })
      this.units = unitsResponse?.data || []
      
      this.loading = false
    } catch (error) {
      console.error('Error loading product data:', error)
      this.loading = false
    }
  },

  computed: {
    filteredItems() {
      return this.suggestions.filter(i => {
        return i.toLowerCase().indexOf(this.tag.toLowerCase()) !== -1;
      });
    },
  },

  watch: {
    'product.category_id': function (newCategoryId) {
      if (newCategoryId && this.product.buy) {
        const selectedCategory = this.categories.find(c => c.id === newCategoryId)
        if (selectedCategory && selectedCategory.sale_margin_percentage) {
          const margin = selectedCategory.sale_margin_percentage
          this.product.sell = Math.ceil(((this.product.buy * (margin/100)) + this.product.buy))
        }
      }
    },
    'product.buy': function () {
      if (this.product.category_id) {
        const selectedCategory = this.categories.find(c => c.id === this.product.category_id)
        if (selectedCategory && selectedCategory.sale_margin_percentage) {
          const margin = selectedCategory.sale_margin_percentage
          this.product.sell = Math.ceil(((this.product.buy * (margin/100)) + this.product.buy))
        }
      }
    },
    tags: function () {
      this.product.tags = this.tags.map(tag => tag.text).join(',')
    }
  },
}
</script>

<style scoped>
.product-form-content {
  padding: 0.5rem;
}

.form-label {
  font-weight: 600;
  color: #555;
}

.card {
  transition: all 0.3s ease;
  overflow: hidden;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.05) !important;
}

.input-group-text {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .form-label {
    font-size: 0.95rem;
  }

  .input-group-text {
    padding: 0.25rem 0.5rem;
  }
}
</style>

<style scoped>

</style>
